import type { ExpandedState, OnChangeFn, SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import type { SalesOpportunitySearchParams } from "../schemas"
import { useSalesOpportunitySearchStore } from "../stores/useSalesOpportunitySearchStore"

import type { SalesPipeline } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

type SalesOpportunityListRoute = "/app/sales/" | "/app/tablet/sales/"

interface UseSalesOpportunityListParams {
  route: SalesOpportunityListRoute
}

interface UseSalesOpportunityListReturn {
  salesOpportunities: SalesPipeline[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expandedState: ExpandedState
  searchParams: SalesOpportunitySearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof SalesOpportunitySearchParams>(
    key: K,
    value: SalesOpportunitySearchParams[K]
  ) => void
  handleFilterRemove: (key: keyof SalesOpportunitySearchParams, value?: string | number) => void
  handleFilterClear: () => void
  handleScroll: () => void
  onExpandedChange: OnChangeFn<ExpandedState>
}

const FILTER_KEYS: (keyof SalesOpportunitySearchParams)[] = [
  "companyIds",
  "createdByUserIds",
  "statuses",
  "quoteDateFrom",
  "quoteDateTo",
]

export const useSalesOpportunityList = ({ route }: UseSalesOpportunityListParams): UseSalesOpportunityListReturn => {
  const store = useSalesOpportunitySearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: salesOpportunities,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<SalesPipeline, SalesOpportunitySearchParams>({
    route,
    store,
    filterKeys: FILTER_KEYS,
  })

  return {
    salesOpportunities,
    isFetching,
    error,
    sorting,
    expandedState: expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    onExpandedChange: handleExpandedChange,
  }
}
